import { fetchJSON as fetchAPI } from './client';

export type Dependency = { key: string; id: string; name: string; type: string; status: string; description?: string };
export type Objective = { key: string; name: string; category: string; description?: string; mandatory: boolean };
export type WorkspaceTab = { key: string; name: string; route: string; description?: string; status: string; configurable?: boolean };
export type HeaderField = { key: string; name: string; description?: string; status: string };
export type KPIRibbonSpec = { key: string; name: string; unit?: string; supports?: string[]; status: string };
export type ProviderInterface = { key: string; name: string; interface: string; description?: string; status: string };
export type FrontendFeature = { key: string; name: string; category: string; description?: string; status: string };
export type DatabaseTable = { key: string; name: string; service: string; description?: string; has_rls: boolean };
export type BackendAPI = { key: string; method: string; path: string; description?: string; auth_required: boolean };
export type PerformanceBudget = { key: string; surface: string; target: string; unit: string; description?: string };
export type QualityGate = { key: string; name: string; command?: string; description?: string; blocking: boolean };
export type OutOfScopeItem = { key: string; name: string; reason?: string };
export type SuccessCriterion = { key: string; criterion: string; validation?: string; mandatory: boolean };

export type SprintDashboard = {
  sprint_id: string; tenant_id: string; version: string; priority: string; applies_to: string[];
  dependencies: number; objectives: number; workspace_tabs: number; header_fields: number;
  kpi_ribbon_metrics: number; provider_interfaces: number; frontend_features: number;
  database_tables: number; backend_apis: number; performance_budgets: number;
  quality_gates: number; out_of_scope: number; success_criteria: number; generated_at: string;
};

export type SprintValidation = {
  valid: boolean; workspace_fully_responsive: boolean; accessibility_passes_wcag_aa: boolean;
  rbac_enforced: boolean; tenant_isolation_verified: boolean; all_apis_documented: boolean;
  tests_pass: boolean; ci_passes: boolean; performance_budgets_achieved: boolean;
  no_placeholder_ui: boolean; no_hardcoded_business_logic: boolean; gaps?: string[]; evaluated_at: string;
};

export type Dev104Status = {
  sprint_id: string; version: string; priority: string; workspace_tabs: number;
  provider_interfaces: number; kpi_ribbon_metrics: number; quality_gates: number;
  product_operating_system: boolean; production_ready: boolean; provider_driven: boolean; status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';
const fetchG = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);

export const productWorkspaceDev104Api = {
  dashboard: () => fetchG<SprintDashboard>('/dev104/dashboard'),
  dependencies: () => fetchG<{ dependencies: Dependency[] }>('/dev104/dependencies').then((r) => r.dependencies),
  objectives: () => fetchG<{ objectives: Objective[] }>('/dev104/objectives').then((r) => r.objectives),
  workspaceTabs: () => fetchG<{ tabs: WorkspaceTab[] }>('/dev104/workspace-tabs').then((r) => r.tabs),
  productHeader: () => fetchG<{ fields: HeaderField[] }>('/dev104/product-header').then((r) => r.fields),
  kpiRibbon: () => fetchG<{ kpis: KPIRibbonSpec[] }>('/dev104/kpi-ribbon').then((r) => r.kpis),
  serviceArchitecture: () => fetchG<{ providers: ProviderInterface[] }>('/dev104/service-architecture').then((r) => r.providers),
  frontendFeatures: () => fetchG<{ features: FrontendFeature[] }>('/dev104/frontend-features').then((r) => r.features),
  databaseSchema: () => fetchG<{ tables: DatabaseTable[] }>('/dev104/database-schema').then((r) => r.tables),
  backendAPIs: () => fetchG<{ apis: BackendAPI[] }>('/dev104/backend-apis').then((r) => r.apis),
  performance: () => fetchG<{ budgets: PerformanceBudget[] }>('/dev104/performance').then((r) => r.budgets),
  qualityGates: () => fetchG<{ gates: QualityGate[] }>('/dev104/quality-gates').then((r) => r.gates),
  outOfScope: () => fetchG<{ items: OutOfScopeItem[] }>('/dev104/out-of-scope').then((r) => r.items),
  successCriteria: () => fetchG<{ criteria: SuccessCriterion[] }>('/dev104/success-criteria').then((r) => r.criteria),
  validate: (attrs: Record<string, boolean>) =>
    fetchG<SprintValidation>('/dev104/validate', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attrs }) }),
  status: () => fetchG<Dev104Status>('/dev104/status'),
};
